/* Dashboard-top notifications: milestone celebrations + payday reminders.
 *
 * - Milestones: fetches /vault-milestones/pending and shows a stacked
 *   celebration banner. User can dismiss individual tiles or all at once.
 * - Payday reminder: computes whether TODAY is a payday-Friday (using the
 *   same 2026-08-21 anchor and cadences) and, if so, shows a banner listing
 *   what's about to pull.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, fmtMoney } from "@/lib/api";
import { PartyPopper, X, Bell, CalendarClock, Check } from "lucide-react";

const ANCHOR = new Date("2026-08-21T12:00:00");
const DAY_MS = 24 * 60 * 60 * 1000;
const CADENCE_LABEL = {
  monthly_payday: "monthly · on payday",
  every_other_paycheck: "every other payday",
  biweekly_friday: "every payday",
};

function ymd(d) { return d.toISOString().slice(0, 10); }
function noonToday() { const d = new Date(); d.setHours(12, 0, 0, 0); return d; }

function firstPaydayInMonth(year, month) {
  if (year < ANCHOR.getFullYear() ||
      (year === ANCHOR.getFullYear() && month < ANCHOR.getMonth())) return null;
  if (year === ANCHOR.getFullYear() && month === ANCHOR.getMonth()) return new Date(ANCHOR);
  const firstOfMonth = new Date(year, month, 1, 12, 0, 0);
  const days = Math.floor((firstOfMonth - ANCHOR) / DAY_MS);
  const periods = Math.ceil(days / 14);
  const payday = new Date(ANCHOR.getTime() + periods * 14 * DAY_MS);
  return (payday.getFullYear() === year && payday.getMonth() === month) ? payday : null;
}

function firesToday(cadence, today) {
  const key = ymd(today);
  if (cadence === "monthly_payday") {
    const p = firstPaydayInMonth(today.getFullYear(), today.getMonth());
    return p && ymd(p) === key;
  }
  const step = cadence === "biweekly_friday" ? 14 : 28;
  if (today < ANCHOR) return false;
  const days = Math.floor((today - ANCHOR) / DAY_MS);
  return days >= 0 && days % step === 0;
}


// ------ Milestone celebration --------------------------------------------

function MilestoneBanner() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vault-milestones/pending");
      setPending(data.milestones || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismissOne = async (id) => {
    setPending((prev) => prev.filter(m => m.id !== id));
    try { await api.post(`/vault-milestones/${id}/acknowledge`); } catch { /* ok */ }
  };
  const dismissAll = async () => {
    setPending([]);
    try { await api.post("/vault-milestones/acknowledge-all"); } catch { /* ok */ }
  };

  if (loading || pending.length === 0) return null;

  return (
    <div className="rounded-2xl bg-gradient-to-br from-gold via-[#B37F35] to-[#8B5E1A] text-white p-5 sm:p-6 mb-4"
         data-testid="milestone-banner">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-cream/80">
            <PartyPopper className="w-3 h-3" strokeWidth={2} /> Milestone
          </div>
          <h3 className="font-serif text-xl sm:text-2xl mt-1.5">
            {pending.length === 1
              ? "One of your vaults just crossed a milestone."
              : `${pending.length} of your vaults hit new milestones.`}
          </h3>
        </div>
        {pending.length > 1 && (
          <button onClick={dismissAll}
            className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
            data-testid="milestone-dismiss-all">
            <Check className="w-3 h-3" strokeWidth={2} /> Dismiss all
          </button>
        )}
      </div>
      <ul className="mt-3 space-y-2" data-testid="milestone-list">
        {pending.map((m) => (
          <li key={m.id}
              data-testid={`milestone-item-${m.id}`}
              className="flex items-center justify-between gap-3 rounded-lg bg-white/12 backdrop-blur-sm px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{m.vault_name}</div>
              <div className="text-[10px] text-white/80 uppercase tracking-wider">
                crossed ${m.tier.toLocaleString()} · balance now {fmtMoney(m.balance_at_crossing)}
              </div>
            </div>
            <button onClick={() => dismissOne(m.id)}
              className="p-1.5 rounded hover:bg-white/20 shrink-0"
              aria-label={`Dismiss ${m.vault_name} milestone`}
              data-testid={`milestone-dismiss-${m.id}`}>
              <X className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ------ Payday reminder --------------------------------------------------

function PaydayReminderBanner() {
  const { user } = useAuth();
  const [customs, setCustoms] = useState([]);
  const [dismissedKey, setDismissedKey] = useState(() =>
    localStorage.getItem("wg_payday_dismissed") || "");
  const today = useMemo(() => noonToday(), []);
  const todayKey = ymd(today);

  useEffect(() => {
    api.get("/baby-funds")
      .then(({ data }) => setCustoms((data || []).filter(b => b.auto?.enabled).map(b => ({ name: `Baby · ${b.name}`, amount: b.auto.amount, cadence: "monthly_payday" }))))
      .catch(() => {});
  }, []);

  const items = useMemo(() => {
    const list = [];
    const push = (label, status, defaultCadence, defaultAmt) => {
      if (!status?.applicable || status.enabled === false) return;
      list.push({
        label, amount: Number(status.contribution_amount ?? defaultAmt),
        cadence: status.cadence || defaultCadence,
      });
    };
    push("Retirement",   user?.retirement_contribution,    "monthly_payday", 100);
    push("Rainy-day",    user?.job_loss_contribution,      "monthly_payday", 100);
    push("Car Emergency",user?.car_emergency_contribution, "every_other_paycheck", 50);
    customs.forEach(c => list.push({ label: c.name, amount: Number(c.amount), cadence: c.cadence }));
    return list.filter(i => firesToday(i.cadence, today));
  }, [user, customs, today]);

  if (items.length === 0) return null;
  if (dismissedKey === todayKey) return null;

  const total = items.reduce((s, i) => s + i.amount, 0);

  const dismiss = () => {
    localStorage.setItem("wg_payday_dismissed", todayKey);
    setDismissedKey(todayKey);
  };

  return (
    <div className="rounded-2xl bg-ink text-cream p-5 sm:p-6 mb-4"
         data-testid="payday-reminder">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-gold">
            <Bell className="w-3 h-3" strokeWidth={2} /> Payday
          </div>
          <h3 className="font-serif text-xl sm:text-2xl mt-1.5">
            Today&apos;s the day — {today.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </h3>
          <p className="text-xs text-sage-light mt-0.5">
            {items.length} automatic {items.length === 1 ? "pull" : "pulls"} landing on Spending.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Total</div>
          <div className="font-mono text-2xl sm:text-3xl mt-0.5" data-testid="payday-reminder-total">
            {fmtMoney(total)}
          </div>
        </div>
      </div>
      <ul className="mt-3 flex flex-wrap gap-2" data-testid="payday-reminder-items">
        {items.map((i, idx) => (
          <li key={idx}
              data-testid={`payday-reminder-item-${idx}`}
              className="inline-flex items-center gap-1.5 text-xs bg-white/10 rounded-md px-2 py-1">
            <span className="text-cream">{i.label}</span>
            <span className="text-[9px] uppercase tracking-wider text-sage">
              · {CADENCE_LABEL[i.cadence] || i.cadence}
            </span>
            <span className="font-mono text-gold">{fmtMoney(i.amount)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex justify-end">
        <button onClick={dismiss}
          className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
          data-testid="payday-reminder-dismiss">
          <Check className="w-3 h-3" strokeWidth={2} /> Got it
        </button>
      </div>
    </div>
  );
}


export default function DashboardBanners() {
  return (
    <>
      <MilestoneBanner />
      <PaydayReminderBanner />
    </>
  );
}


